import React, {
  CSSProperties,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { CheckboxProps, Dimmer, Divider, Icon, Loader } from "semantic-ui-react";

import { BLClickHandler } from "react-lib/appcon/common/ButtonLoadCheck";

import SubDrugNameFormat from "react-lib/apps/HISV3/common/SubDrugNameFormat";

import { DisplayResult } from "../LAB/CardOPDLabSummary";
import IconTurnAroundTime from "../LAB/IconTurnAroundTime";

import ButtonCallPharmacist from "./ButtonCallPharmacist";
import CardHomeMedDetailUX from "./CardHomeMedDetailUX";

import {
  ORDER_ITEM_STATUS_LABEL,
  RESULT_SUMMARY,
  RESULT_SUMMARY_LABEL,
} from "../IME/sequence/ImagingHandler";

import { getDrugStatusName } from "../TPD/TPDInterface";

import { getOrdinal } from "react-lib/utils/dateUtils";

import CONFIG from "config/config";

// Types
type CardHomeMedDetailProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => any) => any;

  displayAbnormal?: boolean;
  index: number;
  items: Partial<{
    [key: string]: any;
    drugTransportNotAllowedIds?: number[];
    encounterStatus: string;
    items: Record<string, any>[];
    loading?: boolean;
    order_items: Record<string, any>[];
    order_type_label?: "drug" | "imagingorder" | "rehabilitationorder" | "supply";
    summary: {
      order_summary_detail: string;
      specific_status: Record<string, any>;
      specific_type: Record<string, any>;
    };
    telephar_type: string;
  }>[];
  languageUX?: string;
  // options
  masterOptions?: any;
  // callback
  onClose?: (e: SyntheticEvent) => any;
};

type Styles = Record<"critical" | "divider" | "labBox" | "warningIcon", CSSProperties>;

const styles: Styles = {
  critical: {
    backgroundColor: "#f5baba",
    border: "2px solid #d43939",
    borderRadius: "5px",
    color: "#d43939",
    fontSize: "0.95rem",
    marginRight: "0.75rem",
    padding: "0 0.5rem",
  },
  divider: { margin: "0.25rem 0 0.75rem" },
  labBox: {
    alignItems: "center",
    display: "flex",
    marginRight: "-6rem",
    marginTop: "-2px",
  },
  warningIcon: {
    background: "white",
    borderRadius: "500rem",
    fontSize: "1.2em",
    height: "15px",
    left: "0px",
    marginTop: "4px",
    position: "absolute",
    width: "15px",
  },
};

const DETAIL_REGEX = /(.*?):(.\d+)/;

const CardHomeMedDetail = (props: CardHomeMedDetailProps) => {
  const [telepharType, setTelepharType] = useState("NONE");

  // Memo Effect
  const data = useMemo(
    () =>
      // console.log(" effect data  props.items: ", props.items)
      props.items[props.index] || {},
    [props.index, props.items]
  );

  // Effect
  useEffect(() => {
    setTelepharType(data.telephar_type || "");
  }, [data.telephar_type]);

  // Memo callback
  const productType = useMemo(
    (): "centrallaborder" | "drug" | "imagingorder" | "rehabilitationorder" | "supply" =>
      data.order_type_label || "drug",
    [data]
  );

  const orderItems = useMemo(() => data.order_items || data.items || [], [data]);

  const drugDetail = useMemo(() => {
    // console.log(" effect drugDetail data: ", data)
    const {
      full_access_number: fullAccessNumber,
      order_id: orderId,
      summary,
      supply_transfer_status: supplyTransferStatus,
    } = data;
    const detail = summary?.order_summary_detail || "";

    /*
      รายการสั่งยากลับบ้านผู้ป่วยนอก:2312000063<br/><b>Paracetamol (Paracetamol) 325 mg Tablet</b>
      <font color="gray">1 Tablet รับประทาน <b>{intl.formatMessage({ id: "ทุก 6 ชั่วโมง" })}</b> <b>{intl.formatMessage({ id: "เฉพาะเมื่อมีอาการปวด" })}</b> <br/> <b>Duration</b> 1 days</font>
    */
    const matches = DETAIL_REGEX.exec(detail);
    const groups = matches ? { code: matches[2], title: matches[1] } : {};
    const formattedStatusName = getDrugStatusName(summary?.specific_status.name);
    const statusName: string =
      productType === "centrallaborder"
        ? summary?.specific_status.label
        : summary?.specific_status.name;

    let groupCode = productType === "imagingorder" ? orderId : groups.code?.trim() || "";
    let groupTitle = groups.title?.trim() || "";

    if (productType === "imagingorder") {
      groupTitle = "รายการคำสั่ง Imaging";
    } else if (productType === "centrallaborder") {
      groupTitle = "Lab ID";
    } else if (productType === "rehabilitationorder") {
      groupTitle = "รายการคำสั่ง Rehab";
    }

    if (productType === "imagingorder") {
      groupCode = orderId;
    } else if (productType === "centrallaborder") {
      groupCode = fullAccessNumber;
    } else if (productType === "rehabilitationorder") {
      groupCode = " ";
    }

    return {
      code: groupCode,
      names: orderItems.map((item) => item.drug_name_from_format as string),
      specificType: summary?.specific_type.name,
      statusName: productType === "drug" ? formattedStatusName : statusName,
      supplyTransferStatus,
      title: groupTitle,
    };
  }, [data, productType]);

  // Callback
  const getDrugStatusLabel = (statusLabel: string) => {
    // #console.log('status: ', status);
    const consultStatus = data.consult_status;
    // #console.log('consultStatus: ', consultStatus);

    const paymentStatus = {
      PAID: "(ชำระเงินแล้ว)",
      READY: "(รอชำระเงิน)",
    } as any;

    const orderStatus = {
      CHECKED: paymentStatus[data.order_payment_status],
      RECEIVED: {
        CALLING: "อยู่ในสายกับเภสัชกร",
        IDLE: "รอต่อสายหาเภสัชกร",
        ON_CALL: "อยู่ในสายกับเภสัชกร",
      },
      TRANSPORTED: "รอพยาบาลรับยา",
    } as any;

    let status = statusLabel;
    let remark = orderStatus[status];

    if (status === "RECEIVED") {
      remark = orderStatus[status][consultStatus];

      // * Intra TelePharmacy order status RECEIVED และ consult status FINISH ให้แสดง status DELIVERED
      if (consultStatus === "FINISH") {
        status = "DELIVERED";
      }
    }

    return { remark, status };
  };

  // Handler
  const handleChangeValue = useCallback(
    (e: any, v: CheckboxProps) => {
      const value = telepharType === v.name ? "NONE" : v.name;

      setTelepharType(value || "");

      props.onEvent({
        message: "DrugOrderUpdateCallTelephar",
        params: { id: data.id, data: { telephar_type: value } },
      });
    },
    [data, telepharType]
  );

  const handleCall: BLClickHandler = useCallback(
    (e) => {
      // start video call
      console.log(drugDetail.code);

      props.setProp("openVideoCallModal", true);
      props.setProp("closeOverriding", true);
      props.setProp("videoCallRoom", drugDetail.code);

      props.onEvent({
        message: "DrugOrderUpdateCallTelephar",
        params: {
          id: data.id,
          data: { consult_status: "CALLING" },
        },
      });

      props.onClose?.(e);
    },
    [data, drugDetail]
  );

  // Memo
  const statusLabel = useMemo(() => {
    // console.log(" effect statusLabel drugDetail: ", drugDetail)
    let status = drugDetail.statusName;
    // #console.log('drugDetail: ', drugDetail);
    // #console.log('data: ', data);

    // Supply
    if (status === "PRINTED" && productType === "supply") {
      status = drugDetail.supplyTransferStatus;
    }

    // Drug
    const drugStatusLabel = getDrugStatusLabel(status);

    status = drugStatusLabel.status;

    // Imaging
    // if (productType === "imagingorder") {
    //   status = getStatusImagingOrder(orderItems as { [key: string]: any; status: string }[]).label;
    // }

    let { remark } = drugStatusLabel;

    remark = remark ? ` : ${remark}` : "";

    // console.log(" `${status} ${remark || }`; ", `${status} ${remark || ""}`)
    return productType === "drug" && CONFIG.ENABLE_INTRA_TELEPHAR ? `${status}${remark}` : status;
  }, [drugDetail, orderItems, productType]);

  const allowedEdit = useMemo(
    () => !["DELIVERED", "RECEIVED", "TRANSPORTED"].includes(drugDetail.statusName),
    [drugDetail.statusName]
  );

  const showCheck = useMemo(
    () =>
      drugDetail.specificType === "HOME_OPD" &&
      productType === "drug" &&
      !!CONFIG.ENABLE_INTRA_TELEPHAR,
    [drugDetail.specificType, productType]
  );

  const groupOrderItems = useMemo(() => {
    if (productType === "imagingorder") {
      const grouped = groupOrderByItemStatus(orderItems);

      return Object.entries(grouped).map(([key, value]) => ({ items: value, status: key }));
    }

    return [{ items: orderItems, status: statusLabel }];
  }, [orderItems, productType, statusLabel]);

  const itemsElement = useMemo(
    () =>
      groupOrderItems.map((order, pIndex) => (
        <>
          <div
            style={{
              margin: pIndex === 0 ? "-1.8rem 0 0.95rem" : "0.8rem 0 0.8rem",
              textAlign: "left",
              ...(productType === "rehabilitationorder" && { marginBottom: "-2.5rem" }),
            }}
          >
            {order.items.length > 0 && productType !== "rehabilitationorder" && (
              <strong>
                สถานะ :{" "}
                <span style={{ fontWeight: "normal", marginLeft: "0.5rem" }}>{order.status}</span>
              </strong>
            )}
          </div>

          <ul style={{ margin: "-0.15rem 0 0", padding: "0 7rem 0 1.5rem" }}>
            {order.items.map((item: any, index: number) => {
              // #const unitOptions: any[] = props.masterOptions?.unit || [];
              // const unitName: string =
              //   unitOptions.find((option: any) => option.value === item.unit)?.text || "";
              const names: string[] = drugDetail.names;
              const unit: string = item.unit || "";
              const ordinal = getOrdinal(item.no || 0);

              const configMapping = {
                centrallaborder: {
                  isAbnormal: false,
                  name: (
                    <span
                      style={{ color: item.urgency === "STAT" ? "red" : "" }}
                    >{`[${item.lab_code}] ${item.name}`}</span>
                  ),
                  prefix: "",
                  quantity: (
                    <div style={styles.labBox}>
                      {item.result_value ? (
                        <>
                          {!!item.result_critical && (
                            <>
                              <div style={styles.critical}>ค่าวิกฤต</div>
                              <label>
                                {item.result_value} {item.unit_name}
                              </label>
                              <DisplayResult
                                columnsStyle="auto auto"
                                critical={item.result_critical}
                                labelStyle={{ fontSize: "0.525rem", marginTop: "-2.5px" }}
                                status={item.result_status}
                                statusStyle={{ gridTemplateColumns: "2rem 1.75rem" }}
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <IconTurnAroundTime
                          exceedTime={item.exceed_turn_around_time}
                          style={{ marginRight: "0.25rem", marginTop: "-4.5px" }}
                          disabled
                          showExceedTime
                        />
                      )}
                    </div>
                  ),
                  unit: "",
                },
                drug: {
                  isAbnormal: false,
                  name: (
                    <div style={{ fontWeight: "lighter" }}>
                      <SubDrugNameFormat
                        onEvent={props.onEvent}
                        name={names[index] || item.drug_name_from_format}
                        product={item.product}
                      />
                    </div>
                  ),
                  prefix: "",
                  quantity: Number(item.quantity) || "",
                  // * เม็ด (TAB)
                  unit: item.stock_unit_name, // /\((.*)\)/.exec(unitName)?.[1].toLowerCase(),
                },
                imagingorder: {
                  isAbnormal: RESULT_SUMMARY_LABEL[item.result_summary] === RESULT_SUMMARY.ABNORMAL,
                  name: item.name_code,
                  prefix: "จำนวน",
                  quantity: item.quantity,
                  unit: ` ${unit.toString().split(" : ")[1] || "-"}`,
                },
                rehabilitationorder: {
                  isAbnormal: false,
                  name: (
                    <>
                      {ordinal.number}
                      <sup>{ordinal.suffix}</sup> {item.name || "-"}
                    </>
                  ),
                  prefix: "",
                  quantity: "",
                  unit: "",
                },
                supply: {
                  isAbnormal: false,
                  name: item.name,
                  prefix: "จำนวน",
                  quantity: item.quantity_request,
                  unit: item.stock_unit,
                },
              };

              const config = configMapping[productType];

              return (
                <li key={item.id} style={props.displayAbnormal ? { marginLeft: "1.45rem" } : {}}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr max-content",
                      lineHeight: 1.75,
                    }}
                  >
                    <div style={{ textAlign: "left" }}>
                      {config.isAbnormal && (
                        <div style={styles.warningIcon}>
                          <div style={{ marginLeft: "-2px", marginTop: "-6px" }}>
                            <Icon color="red" name="warning circle" />
                          </div>
                        </div>
                      )}
                      {config.name}
                    </div>
                    <div
                      style={{ minWidth: "max-content", paddingLeft: "2rem", textAlign: "right" }}
                    >
                      {config.prefix} {config.quantity} {config.unit}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )),
    [
      drugDetail,
      groupOrderItems,
      orderItems,
      productType,
      props.displayAbnormal,
      props.masterOptions?.unit,
    ]
  );

  const buttonCall = useMemo(
    () => (
      <ButtonCallPharmacist
        setProp={props.setProp}
        consultStatus={data.consult_status}
        encounterStatus={data.encounterStatus}
        orderPaymentStatus={data.order_payment_status}
        productType={productType}
        size="small"
        specificType={drugDetail.specificType}
        statusName={drugDetail.statusName}
        telepharType={data.telephar_type}
        onCall={handleCall}
      />
    ),
    [data, drugDetail, handleCall, productType]
  );

  return (
    <div style={{ marginBottom: "-1.5rem" }}>
      <Dimmer active={data.loading} inverted>
        <Loader inverted />
      </Dimmer>

      {props.index !== 0 && <Divider style={styles.divider} />}

      <CardHomeMedDetailUX
        buttonCall={!data.loading && buttonCall}
        disabledCheck={!allowedEdit}
        drugDetail={drugDetail}
        hasDrugTransportNotAllowed={data.drugTransportNotAllowedIds?.length}
        itemsElement={itemsElement}
        languageUX={props.languageUX}
        productType={productType}
        showCheck={showCheck}
        status={statusLabel}
        telepharType={telepharType}
        // callback
        onChangeValue={handleChangeValue}
      />
    </div>
  );
};

const groupOrderByItemStatus = (items: Record<string, any>[]) => {
  const groupItems: Record<string, any[]> = {};

  for (const item of items) {
    const status: string = ORDER_ITEM_STATUS_LABEL[item.status];

    groupItems[status] ??= [];
    groupItems[status].push(item);
  }

  return groupItems;
};

CardHomeMedDetail.displayName = "CardHomeMedDetail";

export default React.memo(CardHomeMedDetail);
